import { useMemo } from 'react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { Wellbore } from 'types/api-responses';
import { formatFixed } from 'utils/number';
import styles from './wellbores-table.module.scss';

/**
 * Whether the bore's geometry is the two filed ends and nothing between (the backend's
 * "Stick" kind, Story 7): no depths to show, and counted on the card banner.
 * @param {Wellbore} row The wellbore row.
 * @return {boolean} True for a stick.
 */
export const isStickRow = (row: Wellbore) => row.geometrySourceKind === 'Stick';

// One point of an expanded wellbore, pre-formatted so search matches what is on screen;
// a stick shows its two ends with the depth columns empty.
interface WellborePointRow extends Record<string, unknown> {
  id: string;
  pointName: string;
  latitude: string;
  longitude: string;
  measuredDepth: string;
  depth: string;
  tvdss: string;
  source: string;
}

const POINTS_PAGE_SIZES = [25, 50, 100];

const POINT_COLUMNS: DataTableColumnDef<WellborePointRow>[] = [
  {
    id: 'pointName',
    accessorKey: 'pointName',
    header: 'Point Name',
    size: 160,
  },
  { id: 'latitude', accessorKey: 'latitude', header: 'Latitude', size: 150 },
  { id: 'longitude', accessorKey: 'longitude', header: 'Longitude', size: 150 },
  {
    id: 'measuredDepth',
    accessorKey: 'measuredDepth',
    header: 'Measured Depth',
    size: 160,
  },
  { id: 'depth', accessorKey: 'depth', header: 'Depth (TVDKB)', size: 130 },
  { id: 'tvdss', accessorKey: 'tvdss', header: 'TVDSS', size: 130 },
  { id: 'source', accessorKey: 'source', header: 'Source', size: 120 },
];

/**
 * The expanded row: the bore's points, derived from its geometry at read time, on the
 * shared DataTable so a long survey searches and pages like every other table rather
 * than rendering hundreds of rows at once.
 * @param {Object} props The wellbore whose points to list, as `row`.
 * @return {JSX.Element} The points sub-table.
 */
export const WellborePointsRows = ({ row }: { row: Wellbore }) => {
  const points = useMemo(
    () => row.surveyGeometryPoints?.surveyGeometry ?? [],
    [row.surveyGeometryPoints]
  );
  const rows = useMemo<WellborePointRow[]>(() => {
    const last = points.length - 1;
    // An operator's edit, whatever its label (Synthetic Survey, or User Edited Survey for
    // an edit of a filed survey, INS-12936 UAT item 2).
    const source = row.geometrySource === 'user_edited' ? 'User' : 'Vendor';
    // On a stick the vendor filed no depths; a zero here is a placeholder, not a value.
    const stick = isStickRow(row);
    // Depth is TVDSS read through the surface hole's own TVDSS (the datum), not the
    // row's KB, so it matches the Wellbore Points editor, whose datum may be a stated KB.
    const datumTvdss = points[0]?.tvdss ?? null;
    const depthOf = (tvdss: number | null | undefined) => {
      if (tvdss === null || tvdss === undefined || datumTvdss === null) {
        return null;
      }
      return datumTvdss - tvdss;
    };
    const num = (value: number | null | undefined, decimals = 0) =>
      formatFixed(value, decimals, '—');
    return points.map((point, index) => ({
      id: `${row.uwi14}-${index}`,
      pointName:
        index === 0 ? 'SHL' : index === last ? 'BHL' : `Point ${index}`,
      latitude: num(point.y, 6),
      longitude: num(point.x, 6),
      measuredDepth: stick ? '—' : num(point.md),
      depth: stick ? '—' : num(depthOf(point.tvdss)),
      tvdss: stick ? '—' : num(point.tvdss),
      source,
    }));
  }, [points, row]);

  if (points.length === 0) {
    return (
      <p className={styles.noPoints}>No points on record for this wellbore.</p>
    );
  }

  return (
    <div className={styles.pointsTable}>
      <DataTable<WellborePointRow>
        data={rows}
        columns={POINT_COLUMNS}
        getRowId={(pointRow) => pointRow.id}
        scroll={{ mode: 'paginated' }}
        pageSizes={POINTS_PAGE_SIZES}
        initialTableState={{ page: 1, pageSize: POINTS_PAGE_SIZES[0] }}
        searchPlaceholder="Search values"
        config={{
          globalSearch: true,
          sorting: false,
          columnFilters: false,
          columnVisibility: false,
          columnReordering: false,
          columnResizing: false,
          columnPinning: false,
          export: false,
        }}
      />
    </div>
  );
};
